import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, ArrowLeft } from 'lucide-react';

const STATUS_LABEL = {
  operational: { text: 'Operasional', cls: 'text-emerald-600 bg-emerald-50 border-emerald-200', Icon: CheckCircle2, dot: 'bg-emerald-500' },
  degraded:    { text: 'Terganggu',  cls: 'text-amber-600 bg-amber-50 border-amber-200',       Icon: AlertTriangle, dot: 'bg-amber-500' },
  down:        { text: 'Tidak Aktif', cls: 'text-rose-600 bg-rose-50 border-rose-200',         Icon: XCircle, dot: 'bg-rose-500' },
};

const OVERALL_HEADLINE = {
  operational: 'Semua Sistem Berjalan Normal',
  degraded: 'Sebagian Sistem Mengalami Gangguan',
  down: 'Terjadi Gangguan Sistem',
};

function formatTime(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}

export default function Status() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get('/status');
      setData(res.data);
    } catch (e) {
      setError('Tidak dapat mengambil status sistem');
      setData({
        status: 'down',
        checked_at: new Date().toISOString(),
        components: [
          { name: 'API', description: 'REST API service', status: 'down', latency_ms: null },
        ],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const overall = data?.status || 'operational';
  const overallMeta = STATUS_LABEL[overall] || STATUS_LABEL.operational;
  const OverallIcon = overallMeta.Icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="status-page">
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={18} /> <span className="text-sm font-medium">Kembali ke Beranda</span>
          </Link>
          <img src="/logo.png" alt="Tokomoe" className="h-8 object-contain" />
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-12 max-w-3xl">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Status Sistem
          </h1>
          <p className="text-slate-600">Pantauan kondisi layanan Tokomoe secara real-time.</p>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2" /> Memuat status...
          </div>
        ) : (
          <>
            <Card className={`border-2 ${overallMeta.cls.replace('bg-', 'border-').replace(/text-\S+/, '').replace(/border-\S+-50/, '')} mb-6`}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${overallMeta.cls}`}>
                  <OverallIcon size={24} />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">{OVERALL_HEADLINE[overall]}</h2>
                  <p className="text-sm text-slate-500">Diperbarui: {formatTime(data?.checked_at)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={fetchStatus} data-testid="status-refresh-btn">
                  <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </CardContent>
            </Card>

            {error && (
              <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
                {error}
              </div>
            )}

            <Card className="border-slate-200">
              <CardContent className="p-0 divide-y divide-slate-100">
                {(data?.components || []).map((c, i) => {
                  const meta = STATUS_LABEL[c.status] || STATUS_LABEL.operational;
                  return (
                    <div key={i} className="flex items-center gap-3 p-4" data-testid={`status-component-${c.name.toLowerCase()}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${meta.dot} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                        {c.description && <p className="text-xs text-slate-500">{c.description}</p>}
                      </div>
                      {typeof c.latency_ms === 'number' && (
                        <span className="text-xs text-slate-500 font-mono">{c.latency_ms}ms</span>
                      )}
                      <Badge variant="outline" className={meta.cls}>{meta.text}</Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 text-center mt-6">
              Halaman ini auto-refresh setiap 30 detik. Untuk laporan gangguan: {' '}
              <a href="mailto:support@tokomoe.com" className="text-[#2D6CDF] hover:underline">support@tokomoe.com</a>
            </p>
          </>
        )}
      </main>
    </div>
  );
}
